import { t } from "adapters/i18n";
import type { Dispatch } from "react-redux";
import pastureService from "services/pastureService";
import { getErrorMessage } from "shared/errorUtils";
import {
  type PastureDispatchAction,
  listFarmCorrals,
  listFarmPastures,
  listFarmRetreats,
  notifyError,
  notifySuccess,
} from "../actions";
import type { RootState } from "../reducers";

export const fetchFarmCorrals =
  () =>
  async (
    dispatch: Dispatch<PastureDispatchAction>,
    getState: () => RootState
  ): Promise<void> => {
    try {
      const { fazenda } = getState();

      if (fazenda.FazendaSelecionada == null) {
        throw new Error(t("common:no-farm-selected"));
      }

      const corrals = await pastureService.listCorralsForFarm(
        fazenda.FazendaSelecionada
      );

      dispatch(listFarmCorrals(corrals.data));
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("locales:fetch-farm-corrals-error")
      );

      dispatch(notifyError({ mensagem: message }));
    }
  };

export const createCorral =
  (
    payload: Omit<Parameters<typeof pastureService.createCorral>[0], "farmId">
  ) =>
  async (
    dispatch: Dispatch<PastureDispatchAction>,
    getState: () => RootState
  ): Promise<void> => {
    try {
      const { fazenda } = getState();

      if (fazenda.FazendaSelecionada == null) {
        throw new Error(t("common:no-farm-selected"));
      }

      await pastureService.createCorral({
        ...payload,
        farmId: fazenda.FazendaSelecionada,
      });

      dispatch(notifySuccess({ mensagem: t("locales:create-corral-success") }));
    } catch (error) {
      const message = getErrorMessage(error, t("locales:create-corral-error"));

      dispatch(notifyError({ mensagem: message }));
    }
  };

export const updateCorral =
  (id: number, payload: Parameters<typeof pastureService.updateCorral>[1]) =>
  async (dispatch: Dispatch<PastureDispatchAction>): Promise<void> => {
    try {
      await pastureService.updateCorral(id, payload);

      dispatch(notifySuccess({ mensagem: t("locales:update-corral-success") }));
    } catch (error) {
      const message = getErrorMessage(error, t("locales:update-corral-error"));

      dispatch(notifyError({ mensagem: message }));
    }
  };

export const deleteCorral =
  (id: number) =>
  async (dispatch: Dispatch<PastureDispatchAction>): Promise<void> => {
    try {
      await pastureService.deleteCorral(id);

      dispatch(notifySuccess({ mensagem: t("locales:delete-corral-success") }));
    } catch (error) {
      const message = getErrorMessage(error, t("locales:delete-corral-error"));

      dispatch(notifyError({ mensagem: message }));
    }
  };

export const fetchFarmPastures =
  () =>
  async (
    dispatch: Dispatch<PastureDispatchAction>,
    getState: () => RootState
  ) => {
    try {
      const { fazenda } = getState();

      if (fazenda.FazendaSelecionada == null) {
        throw new Error(t("common:no-farm-selected"));
      }

      const pastures = await pastureService.listPasturesForFarm(
        fazenda.FazendaSelecionada
      );

      dispatch(listFarmPastures(pastures.data));
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("locales:fetch-farm-pastures-error")
      );

      dispatch(notifyError({ mensagem: message }));
    }
  };

export const fetchFarmRetreats =
  () =>
  async (
    dispatch: Dispatch<PastureDispatchAction>,
    getState: () => RootState
  ) => {
    try {
      const { fazenda } = getState();

      if (fazenda.FazendaSelecionada == null) {
        throw new Error(t("common:no-farm-selected"));
      }

      const retreats = await pastureService.listRetreatsForFarm(
        fazenda.FazendaSelecionada
      );

      const sortedRetreats = retreats.data.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true })
      );

      dispatch(listFarmRetreats(sortedRetreats));
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("locales:fetch-farm-retreats-error")
      );

      dispatch(notifyError({ mensagem: message }));
    }
  };
